import { ErrorHandler, Injectable } from '@angular/core';

/**
 * Global error handler that recovers from lazy-chunk load failures.
 *
 * After a deploy the browser may still run a cached bundle whose lazy chunk hashes no longer exist
 * on the server. Navigating to a lazy route then throws "Failed to fetch dynamically imported
 * module" (a ChunkLoadError) and the navigation dies — the user is stuck with no idea a hard refresh
 * would fix it. This is especially painful in the public solar embed (anonymous visitors, varied
 * caches, inside a cross-site iframe).
 *
 * On such an error we reload the page once so it picks up the freshly-deployed bundle. A short-lived
 * sessionStorage guard prevents an infinite reload loop when the chunk is genuinely missing (e.g.
 * server down): after one reload attempt within the window we fall back to the default handler.
 */
@Injectable()
export class ChunkLoadErrorHandler implements ErrorHandler {
  private static readonly RELOAD_FLAG = 'chunk_reload_attempt';
  private static readonly RELOAD_WINDOW_MS = 10_000;

  // Chunk-load failure messages across browsers/bundlers (Chrome/Firefox/Safari, webpack, esbuild).
  // These are non-standardised engine strings — revisit them if the build tooling changes.
  private static readonly CHUNK_ERROR = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk \S+ failed/i;

  private readonly delegate = new ErrorHandler();

  handleError(error: unknown): void {
    if (this.isChunkLoadError(error) && this.shouldReload()) {
      sessionStorage.setItem(ChunkLoadErrorHandler.RELOAD_FLAG, `${Date.now()}`);
      this.reloadPage();
      return;
    }
    this.delegate.handleError(error);
  }

  /** Extracted so tests can assert the reload without touching the real window.location. */
  protected reloadPage(): void {
    window.location.reload();
  }

  /** True when the error is a lazy-chunk load failure (matched against CHUNK_ERROR). */
  private isChunkLoadError(error: unknown): boolean {
    const message = this.extractMessage(error);
    return message.length > 0 && ChunkLoadErrorHandler.CHUNK_ERROR.test(message);
  }

  /** Unwraps the message from an Error, a string, or a zone.js-style { rejection } wrapper. */
  private extractMessage(error: unknown): string {
    if (!error) {
      return '';
    }
    if (typeof error === 'string') {
      return error;
    }
    if (error instanceof Error) {
      return `${error.name}: ${error.message}`;
    }
    const rejection = (error as { rejection?: unknown }).rejection;
    if (rejection) {
      return this.extractMessage(rejection);
    }
    return `${(error as { message?: unknown }).message ?? ''}`;
  }

  /** True unless we already reloaded within the recent window (avoids an infinite reload loop). */
  private shouldReload(): boolean {
    const last = sessionStorage.getItem(ChunkLoadErrorHandler.RELOAD_FLAG);
    if (!last) {
      return true;
    }
    const elapsed = Date.now() - Number(last);
    return isNaN(elapsed) || elapsed > ChunkLoadErrorHandler.RELOAD_WINDOW_MS;
  }
}
